import {
  CheckIcon,
  ExclamationTriangleIcon,
  EyeOpenIcon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import {
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { capitalCase } from "change-case";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  NewspaperIcon,
  StickyNote,
  UserIcon,
  EllipsisIcon,
  XIcon,
  ArrowRightIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Button as ButtonPrimitive } from "react-aria-components";
import { useDebounceCallback } from "usehooks-ts";

import { AddFeedsToStickySetDialog } from "@/components/AddFeedsToStickySetDialog";
import { Banner } from "@/components/Callout";
import { theme } from "@/components/canvas-editor/components/Container";
import { preventBubble } from "@/components/canvas-editor/utils";
import { CapabilityList } from "@/components/CapabilityList";
import {
  DeleteFeedButton,
  DeleteFeedDialog,
} from "@/components/DeleteFeedDialog";
import {
  DuplicateFeedButton,
  useDuplicateFeedMutation,
} from "@/components/DuplicateFeedButton";
import { HidePostDialog } from "@/components/HidePostDialog";
import { LoadMoreSpinner } from "@/components/LoadMoreSpinner";
import {
  NodeUpdateWaitingBadge,
  NodeUpdatesWaitingBanner,
} from "@/components/NodeAttentionSummary";
import { Page } from "@/components/Page";
import { PinPostDialog } from "@/components/PinPostDialog";
import { AddTagPopover, TagList, TagListItems } from "@/components/TagList";
import { FeedAvatar } from "@/components/ui/avatar";
import { Button, ButtonLink } from "@/components/ui/button";
import {
  ContextMenu,
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { SearchInput } from "@/components/ui/search-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderSortButton,
  TableRow,
} from "@/components/ui/table";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { UserAnalyticsSummary } from "@/components/UserAnalyticsSummary";
import {
  capabilityCatalogueQueryOptions,
  DeprecationCheck,
  GetMyFeedsOptions,
  getRelevantAutoMigrateFeedsQueryOptions,
  getTagQueryOptions,
  getUserDeprecationCheckQueryOptions,
  myFeedsInfiniteQueryOptions,
  nodeAttentionQueryOptions,
  sharedWithMeQueryOptions,
  recentRateLimitsQueryOptions,
  userStatsQueryOptions,
} from "@/lib/api";
import { Feed } from "@/lib/types";
import { cn, formatBigNumber } from "@/lib/utils";

export const Route = createFileRoute("/app/_sidebar-layout/")({
  component: HomeComponent,
  validateSearch: (search: Record<string, unknown>) => {
    // validate and parse the search params into a typed state
    return {
      order: search?.order,
      sort_by: search.sort_by,
      search_term: search.search_term,
      tags: search.tags,
      // Which set of feeds to show, kept in the URL so the choice survives a reload and can
      // be linked to. Normalised to a concrete value HERE rather than in the component, so
      // the loader's prefetch and the component's query agree on a cache key.
      //
      // Absent means "all": a feed somebody shared with you is one of your feeds, and the
      // dashboard is where people look for it. The server still defaults to "owned", so no
      // other caller of /my_feeds changed.
      access:
        search.access === "owned" || search.access === "shared"
          ? search.access
          : "all",
    } as GetMyFeedsOptions;
  },
  loader: ({ context: { queryClient }, location }) => {
    const search = location.search as GetMyFeedsOptions;
    const sortingRaw = localStorage.getItem("dashboard-sorting");
    const sorting = sortingRaw
      ? (JSON.parse(sortingRaw) as {
          id: Sorting;
          desc: boolean;
        })
      : null;

    void queryClient.prefetchQuery(getRelevantAutoMigrateFeedsQueryOptions());

    void Promise.all([
      queryClient.ensureInfiniteQueryData(
        myFeedsInfiniteQueryOptions({
          ...location.search,
          sort_by: search.sort_by ?? sorting?.id,
          order: search.order ?? (sorting?.desc ? "desc" : "asc"),
        }),
      ),
      queryClient.prefetchQuery(userStatsQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Dashboard" }] }),
});

function DashboardAnalytics() {
  const { data: analytics, isLoading } = useSuspenseQuery(
    userStatsQueryOptions(),
  );

  if (isLoading || (analytics.details && analytics.details.length === 0)) {
    return null;
  }

  return (
    <div className="flex">
      <UserAnalyticsSummary
        summary={analytics.summary}
        analytics={analytics.details}
        query={({ days }) => userStatsQueryOptions({ days })}
      />
    </div>
  );
}

function DeprecationCheckBanner() {
  const { data: deprecationCheck } = useQuery(
    getUserDeprecationCheckQueryOptions(),
  );

  if (
    !deprecationCheck ||
    Object.keys(deprecationCheck.algorithms).length === 0
  ) {
    return null;
  }

  return (
    <Link
      to="/app/account"
      hash="#deprecation-check"
      className="no-underline mb-4"
    >
      <Banner variant="warning" className="cursor-pointer-extra">
        <div className="flex flex-1 gap-4 items-center">
          <ExclamationTriangleIcon className="h-5 w-5" />
          You have feeds that are using deprecated features. These features will
          be sunset soon and you must update them!
        </div>
        <div className="flex items-center flex-shrink-0">
          <span>Update your Feeds</span>
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </div>
      </Banner>
    </Link>
  );
}

/**
 * "While you were away": feeds that ran out of their hourly budget for a metered node.
 *
 * This is the banner the whole rate-limit history exists to make possible. A feed over
 * budget goes quiet rather than erroring, so without this the first signal an owner gets
 * is a week of thinner numbers they have no way to explain.
 *
 * Deliberately NOT inside `DashboardBanners`: that component early-returns when there is
 * no deprecation or auto-migrate item, which is precisely the state most owners are in.
 */
function RateLimitBanner() {
  const { data } = useQuery(recentRateLimitsQueryOptions(7));

  const worst = data?.available ? data.feeds[0] : undefined;
  if (!data?.available || !worst) {
    return null;
  }
  const unscored = data.feeds.reduce((sum, feed) => sum + feed.posts_unscored, 0);

  return (
    <Link
      to="/app/feed/$feedId/rate-limits"
      params={{ feedId: `${worst.id}` }}
      className="no-underline mb-4"
    >
      <Banner variant="warning" className="cursor-pointer-extra">
        <div className="flex flex-1 gap-4 items-center">
          <ExclamationTriangleIcon className="h-5 w-5" />
          {data.feeds.length === 1 ? (
            <span>
              <span className="font-medium">
                {worst.name || "A feed"}
              </span>{" "}
              ran out of its hourly budget in the last 7 days, leaving{" "}
              {worst.posts_unscored.toLocaleString()} posts unjudged.
            </span>
          ) : (
            <span>
              {data.feeds.length} of your feeds ran out of their hourly budget in the last
              7 days, leaving {unscored.toLocaleString()} posts unjudged.
            </span>
          )}
        </div>
        <div className="flex items-center shrink-0">
          <span>See what happened</span>
          <ArrowRightIcon className="ml-2 h-4 w-4" />
        </div>
      </Banner>
    </Link>
  );
}

function DashboardBanners() {
  const { data: deprecationCheck } = useQuery(
    getUserDeprecationCheckQueryOptions(),
  );
  const { data: relevantAutoMigrateFeeds } = useQuery(
    getRelevantAutoMigrateFeedsQueryOptions(),
  );

  // `held` is part of the early-return test on purpose: this component used to
  // bail whenever there was no deprecation or auto-migrate item, which would
  // have swallowed the held-update banner in exactly the common case -- an
  // owner with nothing else wrong.
  const held = useQuery(nodeAttentionQueryOptions()).data ?? [];
  const heldCount = held.filter(
    (item) => item.state === "pending_review" && !item.dismissed,
  ).length;

  if (
    (!deprecationCheck?.algorithms ||
      Object.keys(deprecationCheck.algorithms).length === 0) &&
    (!relevantAutoMigrateFeeds || relevantAutoMigrateFeeds.length === 0) &&
    heldCount === 0
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <NodeUpdatesWaitingBanner />
      <DeprecationCheckBanner />
    </div>
  );
}

function MonetizationBadge({
  feed,
}: {
  feed: Feed & { deprecationCheck?: DeprecationCheck };
}) {
  if (feed.deprecationCheck) {
    const message = `Feed is using ${
      feed.deprecationCheck.deprecated_conditions.length + 1
    }  depreacted features that need updates`;

    return (
      <SimpleTooltip message={message}>
        <div className="flex items-center p-1 rounded-md bg-yellow-4 dark:bg-yellowdarka-4 text-yellow-normal w-fit">
          <span className="sr-only">message</span>
          <ExclamationTriangleIcon />
        </div>
      </SimpleTooltip>
    );
  }

  if (
    !feed.algorithm_manifest_id ||
    feed.algorithm_manifest_id !== feed.draft_algorithm_manifest_id
  ) {
    return (
      <SimpleTooltip message="Feed Unpublished">
        <div className="flex items-center p-1 rounded-md bg-yellow-4 dark:bg-yellowdarka-4 text-yellow-normal w-fit">
          <span className="sr-only">Feed Unpublished</span>
          <InfoCircledIcon />
        </div>
      </SimpleTooltip>
    );
  }

  return feed.monetization_available ? (
    <SimpleTooltip message="Monetization enabled">
      <div className="flex items-center p-1 rounded-md bg-green-4 dark:bg-greendarka-4 text-green-normal w-fit">
        <span className="sr-only">Monetization enabled</span>
        <CheckIcon />
      </div>
    </SimpleTooltip>
  ) : (
    <SimpleTooltip message="Set up monetization">
      <Link
        to="/app/feed/$feedId/settings"
        params={{ feedId: `${feed.id}` }}
        className="flex items-center p-1 rounded-md bg-slate-4 dark:bg-slatedarka-4 text-slate-normal w-fit"
        {...preventBubble}
      >
        <span className="sr-only">Enable monetization</span>
        <span className="h-4 w-4 flex items-center justify-center">~</span>
      </Link>
    </SimpleTooltip>
  );
}

/**
 * Marks a row as somebody else's feed that you have been let into.
 *
 * It carries the capability list in its tooltip because "shared" on its own leaves the one
 * question that matters unanswered — whether you may put a post at the top of this feed or
 * only suggest one — and finding out from a 403 is worse than reading it here.
 */
function SharedBadge({ feed }: { feed: Feed }) {
  const { data: catalogue } = useQuery(capabilityCatalogueQueryOptions());

  if (!feed.shared_with_me) {
    return null;
  }

  const labels = (feed.my_capabilities ?? []).map(
    (key) => catalogue?.capabilities?.find((c) => c.key === key)?.label ?? key,
  );

  return (
    <SimpleTooltip message={<CapabilityList labels={labels} />}>
      <span className="shrink-0 rounded-md bg-slate-4 dark:bg-slatedarka-4 px-1.5 py-0.5 text-xs text-slate-dim">
        Shared
      </span>
    </SimpleTooltip>
  );
}

const columns: ColumnDef<Feed & { deprecationCheck?: DeprecationCheck }>[] = [
  {
    accessorKey: "monetization_available",
    id: "monetized",
    size: 20,
    header: ({ column }) => {
      return (
        <div className="flex justify-center items-center gap-2 w-full">
          <div className="w-8 ml-5">
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                const currentSort = column.getIsSorted();
                column.toggleSorting(
                  currentSort === false ? true : currentSort === "asc",
                );
              }}
              className="-ml-3"
            >
              <span className="sr-only">Monetized</span>
              <div>$</div>
            </Button>
          </div>
        </div>
      );
    },
    cell: ({ row }) => (
      <div className="flex justify-center items-center gap-2">
        <MonetizationBadge feed={row.original} />
        <NodeUpdateWaitingBadge feedId={row.original.id} />
      </div>
    ),
  },
  {
    accessorKey: "name",
    cell: ({ row }) => (
      <div className="flex gap-2 items-center">
        <FeedAvatar feed={row.original} className="h-8 w-8" />
        {row.original.display_name || "Untitled Feed"}
        <SharedBadge feed={row.original} />
      </div>
    ),
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="name"
        direction="asc"
        sorting={table.getState().sorting}
      >
        Name
      </TableHeaderSortButton>
    ),
  },
  {
    accessorKey: "tags",
    header: "Tags",
    cell: ({ row }) => <TagList data={row.original} type="feed" />,
  },
  {
    accessorKey: "unique_users",
    cell: ({ row }) => (
      <span className="text-slate-dim">
        {Intl.NumberFormat("en-us").format(row.original.stats.unique_users)}
      </span>
    ),
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="unique_users"
        sorting={table.getState().sorting}
      >
        Users
      </TableHeaderSortButton>
    ),
  },
  {
    accessorKey: "page_renders",
    cell: ({ row }) => (
      <span className="text-slate-dim">
        {Intl.NumberFormat("en-us").format(row.original.stats.page_renders)}
      </span>
    ),
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="page_renders"
        sorting={table.getState().sorting}
      >
        Requests
      </TableHeaderSortButton>
    ),
  },
  {
    accessorKey: "post_renders",
    cell: ({ row }) => (
      <span className="text-slate-dim">
        {Intl.NumberFormat("en-us").format(row.original.stats.post_renders)}
      </span>
    ),
    header: ({ column, table }) => (
      <TableHeaderSortButton
        column={column}
        id="post_renders"
        sorting={table.getState().sorting}
      >
        Post Renders
      </TableHeaderSortButton>
    ),
  },
  {
    id: "actions",
    enableHiding: false,
    header: () => {
      return (
        <span className="flex justify-end items-center">
          <span className="sr-only">Actions</span>
        </span>
      );
    },
    cell: ({ row }) => {
      // Duplicating and deleting are the owner's, not a collaborator's -- the server refuses
      // either way, and a button that only ever 403s is worse than no button.
      const isShared = !!row.original.shared_with_me;

      return (
        <span className="flex gap-1 justify-end md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {!isShared && (
            <div {...preventBubble}>
              <DuplicateFeedButton feedId={row.original.id} size="smIcon" />
            </div>
          )}
          <SimpleTooltip message="View feed configuration">
            <ButtonLink
              to="/app/feed/$feedId/view"
              params={{ feedId: `${row.original.id}` }}
              variant="outline"
              size="smIcon"
            >
              <span className="sr-only">View feed configuration</span>
              <EyeOpenIcon />
            </ButtonLink>
          </SimpleTooltip>

          {!isShared && (
            <div {...preventBubble}>
              <DeleteFeedButton feed={row.original} size="smIcon" />
            </div>
          )}
        </span>
      );
    },
  },
];

function DuplicateFeedMenuItem({ feedId }: { feedId: number }) {
  const onDuplicateMutation = useDuplicateFeedMutation({ feedId });

  return (
    <MenuItem onSelect={() => onDuplicateMutation.mutate()}>Duplicate</MenuItem>
  );
}

type Sorting =
  | "name"
  | "created_at"
  | "page_renders"
  | "unique_users"
  | "post_renders"
  | "monetized";

type UpdaterFn = (
  sort: Array<{ id: Sorting; desc: boolean }> | undefined,
) => Array<{
  id: Sorting;
  desc: boolean;
}>;

function FilterTag({ tagId }: { tagId: number }) {
  const { data: tag } = useQuery(getTagQueryOptions(tagId));
  const navigate = useNavigate({ from: "/app" });
  const queryParams = Route.useSearch() || ({} as GetMyFeedsOptions);

  if (!tag) {
    return null;
  }

  return (
    <ButtonPrimitive
      className={cn(
        theme.surface[tag.color === "slate" ? "default" : tag.color],
        theme.text[tag.color === "slate" ? "default" : tag.color],
        "rounded-lg px-2 py-1 text-sm",
        "flex items-center gap-1.5",
      )}
      onPress={() => {
        navigate({
          to: "/app",
          search: {
            ...queryParams,
            page: 1,
            tags: queryParams.tags?.filter((t) => t !== tagId),
          },
        }).catch(console.error);
      }}
    >
      {tag.display_name}
      <XIcon className="w-3 h-3" />
    </ButtonPrimitive>
  );
}

function MobileFeedCard({
  feed,
  isSelected,
}: {
  feed: Feed;
  isSelected: boolean;
}) {
  const [addTagPopoverOpen, setAddTagPopoverOpen] = useState(false);

  return (
    <div
      key={feed.id}
      data-state={isSelected && "selected"}
      className="flex flex-col gap-4 group cursor-pointer rounded-lg p-4 border border-slate-6 dark:border-slatedark-6"
    >
      <div className="flex flex-col gap-3">
        <TagListItems data={feed} to="/app" />

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FeedAvatar feed={feed} className="h-8 w-8" />
            <div>{feed.display_name}</div>
            <SharedBadge feed={feed} />
          </div>
          <MonetizationBadge feed={feed} />
          <NodeUpdateWaitingBadge feedId={feed.id} />
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2  text-slate-dim">
            <UserIcon className="h-5 w-5" />
            <span className="text-sm">
              {formatBigNumber(feed.stats.unique_users)}
            </span>
          </div>
          <div className="flex items-center gap-2  text-slate-dim">
            <StickyNote className="h-5 w-5" />
            <span className="text-sm">
              {formatBigNumber(feed.stats.post_renders)}
            </span>
          </div>
          <div className="flex items-center gap-2  text-slate-dim">
            <NewspaperIcon className="h-5 w-5" />
            <span className="text-sm">
              {formatBigNumber(feed.stats.page_renders)}
            </span>
          </div>
        </div>

        <DropdownMenu>
          <AddTagPopover
            data={feed}
            type="feed"
            open={addTagPopoverOpen}
            onOpenChange={setAddTagPopoverOpen}
            isAnchor
          >
            <MenuTrigger asChild>
              <Button variant="outline" size="sm">
                Actions
              </Button>
            </MenuTrigger>
          </AddTagPopover>
          <MenuContent
            onCloseAutoFocus={(e) => {
              if (addTagPopoverOpen) {
                e.preventDefault();
              }
            }}
          >
            <MenuItem asChild>
              <Link
                to="/app/feed/$feedId/view"
                params={{ feedId: `${feed.id}` }}
              >
                View
              </Link>
            </MenuItem>
            {!feed.shared_with_me && (
              <>
                <MenuItem onSelect={() => setAddTagPopoverOpen(true)}>
                  Add tag
                </MenuItem>
                <DuplicateFeedMenuItem feedId={feed.id} />
                <MenuSeparator />
                <DeleteFeedDialog feed={feed}>
                  <MenuItem onSelect={(e) => e.preventDefault()}>
                    Delete
                  </MenuItem>
                </DeleteFeedDialog>
              </>
            )}
          </MenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function FeedTable() {
  "use no memo";

  const router = useRouter();
  const queryParams = Route.useSearch() || ({} as GetMyFeedsOptions);
  const {
    data: feeds,
    isPending,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(myFeedsInfiniteQueryOptions(queryParams));

  const navigate = useNavigate({ from: "/app" });
  const { data: deprecationCheck } = useQuery(
    getUserDeprecationCheckQueryOptions(),
  );
  const sorting = useMemo(
    () =>
      queryParams.sort_by
        ? [{ id: queryParams.sort_by, desc: queryParams.order === "desc" }]
        : undefined,
    [queryParams.sort_by, queryParams.order],
  );
  const userAlgos = useMemo(
    () =>
      (feeds?.pages.flatMap((page) => page.user_algos) || []).map((algo) => ({
        ...algo,
        deprecationCheck: deprecationCheck?.algorithms[algo.id],
      })),
    [feeds?.pages, deprecationCheck?.algorithms],
  );
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: userAlgos,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: (updater) => {
      const updated = (updater as UpdaterFn)(sorting);

      localStorage.setItem("dashboard-sorting", JSON.stringify(updated[0]));
      navigate({
        to: "/app",
        replace: true,
        search: {
          ...queryParams,
          sort_by: updated[0]?.id,
          order: updated[0]?.desc ? "desc" : "asc",
        },
      }).catch(console.error);
    },
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
  });

  const [showHidePostDialog, setShowHidePostDialog] = useState(false);
  const [showPinPostDialog, setShowPinPostDialog] = useState(false);
  const [showStickySetDialog, setShowStickySetDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState(queryParams.search_term);
  const debouncedSetQueryParams = useDebounceCallback(
    (v: string) => {
      navigate({
        to: "/app",
        search: { ...queryParams, page: 1, search_term: v },
        replace: true,
      }).catch(console.error);
    },
    1000,
    { trailing: true },
  );

  const searchInput = (
    <SearchInput
      prefix={
        queryParams.tags ? (
          <div className="flex items-center gap-1 mx-2">
            {queryParams.tags.map((tag) => (
              <FilterTag key={tag} tagId={tag} />
            ))}
          </div>
        ) : null
      }
      aria-label="Search for a feed"
      value={searchTerm}
      onChange={(v) => {
        setSearchTerm(v);
        debouncedSetQueryParams(v);
      }}
    />
  );

  const viewing = queryParams.access ?? "all";
  // Two dead buttons on every dashboard in the product is worse than no control, so the
  // facet appears only once something has actually been shared with you. `total_items`
  // because the current page may be all your own feeds.
  // 🔴 `?.feeds?.` AND NOT `?.feeds.` — the object can be present with the array missing.
  // A 401, a proxy's error body or a stubbed response all deliver SOMETHING truthy, and
  // reading `.length` off the absent array throws during render, which the error boundary
  // turns into a blank page rather than a missing button. The sidebar spec catches exactly
  // this, and caught it here.
  const { data: sharedFeeds } = useQuery(sharedWithMeQueryOptions());
  const hasShared = (sharedFeeds?.feeds?.length ?? 0) > 0;

  // One list, filtered -- not three lists. Shared feeds sort, search and page alongside your
  // own because the server does the filtering; this only picks which `access` to ask for.
  // A facet beside the search box, not a banner above the page title. It is only worth
  // showing once something IS shared with you -- otherwise it is two dead buttons on every
  // dashboard in the product.
  const viewToggle = (
    <div
      className="flex shrink-0 gap-1 rounded-md border border-slate-6 p-0.5 dark:border-slatedark-6"
      role="tablist"
      aria-label="Which feeds to show"
    >
      {(
        [
          ["all", "All feeds"],
          ["owned", "Your feeds"],
          ["shared", "Shared with you"],
        ] as const
      ).map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={viewing === value}
          onClick={() =>
            void navigate({
              to: "/app",
              search: { ...queryParams, page: 1, access: value },
            })
          }
          className={
            viewing === value
              ? "rounded px-3 py-1 text-sm font-medium bg-slate-4 dark:bg-slatedark-4"
              : "rounded px-3 py-1 text-sm text-slate-dim hover:bg-slate-3 dark:hover:bg-slatedark-3"
          }
        >
          {label}
        </button>
      ))}
    </div>
  );

  return userAlgos.length > 0 || queryParams.search_term ? (
    <>
      {showHidePostDialog && <HidePostDialog />}
      {showPinPostDialog && <PinPostDialog />}
      <AddFeedsToStickySetDialog
        open={showStickySetDialog}
        onOpenChange={setShowStickySetDialog}
      />
      <div className="flex justify-between items-center">
        <h1 className="scroll-m-20 text-3xl min-[480px]:text-6xl font-extrabold tracking-tight instrument-serif">
          Dashboard
        </h1>
        <div className="flex items-center gap-2">
          <ButtonLink to="/app/create/choice" size="sm">
            Create a feed
          </ButtonLink>
          <DropdownMenu>
            <SimpleTooltip message="Global Actions">
              <MenuTrigger asChild>
                <Button variant="outline" size="smIcon">
                  <span className="sr-only">Global Actions</span>
                  <EllipsisIcon />
                </Button>
              </MenuTrigger>
            </SimpleTooltip>
            <MenuContent>
              <MenuItem onSelect={() => setShowHidePostDialog(true)}>
                Hide post on all feeds
              </MenuItem>
              <MenuItem onSelect={() => setShowPinPostDialog(true)}>
                Add sticky post to all feeds
              </MenuItem>
              <MenuItem onSelect={() => setShowStickySetDialog(true)}>
                Add feeds to a sticky set
              </MenuItem>
            </MenuContent>
          </DropdownMenu>
        </div>
      </div>
      <DashboardAnalytics />

      <div className="flex-1 md:hidden flex flex-col gap-3">
        {hasShared && <div className="flex">{viewToggle}</div>}
        <div className="flex justify-between items-center gap-4">
          {searchInput}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <MenuTrigger asChild>
                <Button variant="outline" className="shrink-0 px-4">
                  Sort by{" "}
                  {capitalCase(table.getState().sorting?.[0]?.id || "--")}
                </Button>
              </MenuTrigger>
              <MenuContent>
                <MenuRadioGroup
                  value={table.getState().sorting?.[0]?.id}
                  onValueChange={(value) => {
                    table.setSorting(() => [
                      {
                        id: value,
                        desc: !["name", "created_at"].includes(value),
                      },
                    ]);
                  }}
                >
                  <MenuRadioItem value="monetized">Monetized</MenuRadioItem>
                  <MenuRadioItem value="name">Name</MenuRadioItem>
                  <MenuRadioItem value="created_at">Created</MenuRadioItem>
                  <MenuRadioItem value="unique_users">Users</MenuRadioItem>
                  <MenuRadioItem value="page_renders">
                    Page Renders
                  </MenuRadioItem>
                  <MenuRadioItem value="post_renders">
                    Post Renders
                  </MenuRadioItem>
                </MenuRadioGroup>
              </MenuContent>
            </DropdownMenu>
            {table.getState().sorting?.[0] && (
              <SimpleTooltip message="Direction">
                <Button
                  variant="outline"
                  size="smIcon"
                  onClick={() => {
                    const sort = table.getState().sorting?.[0];

                    if (!sort) {
                      return;
                    }

                    table.setSorting(() => [{ id: sort.id, desc: !sort.desc }]);
                  }}
                >
                  {table.getState().sorting?.[0]?.desc ? (
                    <ArrowUpIcon />
                  ) : (
                    <ArrowDownIcon />
                  )}
                </Button>
              </SimpleTooltip>
            )}
          </div>
        </div>

        {table.getRowModel().rows.map((row) => {
          return (
            <MobileFeedCard
              key={row.original.id}
              feed={row.original}
              isSelected={row.getIsSelected()}
            />
          );
        })}
      </div>

      <div className="flex-1 hidden md:flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <div className="flex-1">{searchInput}</div>
          {hasShared && viewToggle}
        </div>
        <div className="rounded-md border border-slate-6 dark:border-slatedark-6">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const isDeprecated =
                  deprecationCheck?.algorithms[row.original.id];

                return (
                  <ContextMenu key={row.original.id}>
                    <MenuTrigger asChild>
                      <TableRow
                        data-state={row.getIsSelected() && "selected"}
                        className={cn(
                          "group cursor-pointer",
                          isDeprecated && "deprecation-pulse",
                        )}
                        onClick={(e) => {
                          if (e.metaKey) {
                            window.open(
                              `/app/feed/${row.original.id}/view`,
                              "_blank",
                            );
                            return;
                          }
                          navigate({
                            to: "/app/feed/$feedId/view",
                            params: { feedId: `${row.original.id}` },
                          }).catch(console.error);
                        }}
                        onMouseEnter={() => {
                          router
                            .preloadRoute({
                              to: "/app/feed/$feedId/view",
                              params: { feedId: `${row.original.id}` },
                            })
                            .catch(console.error);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="whitespace-pre">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </MenuTrigger>
                    <MenuContent>
                      <MenuItem asChild>
                        <Link
                          to="/app/feed/$feedId/view"
                          params={{ feedId: `${row.original.id}` }}
                        >
                          View
                        </Link>
                      </MenuItem>
                      <MenuItem asChild>
                        <Link
                          to="/app/feed/$feedId/view"
                          params={{ feedId: `${row.original.id}` }}
                          target="_blank"
                        >
                          Open in new tab
                        </Link>
                      </MenuItem>
                      {!row.original.shared_with_me && (
                        <>
                          <DuplicateFeedMenuItem feedId={row.original.id} />
                          <MenuSeparator />
                          <DeleteFeedDialog feed={row.original}>
                            <MenuItem onSelect={(e) => e.preventDefault()}>
                              Delete
                            </MenuItem>
                          </DeleteFeedDialog>
                        </>
                      )}
                    </MenuContent>
                  </ContextMenu>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
      {hasNextPage && (
        <LoadMoreSpinner fetchNextPage={fetchNextPage} isLoading={isPending} />
      )}
    </>
  ) : (
    !isPending && (
      <>
        {/* The selector belongs here too. Someone who owns nothing and has been added to
            one feed used to land on a dead end that said "No feeds!" and gave them no way
            to reach the feed that had, in fact, been shared with them. */}
        {hasShared && <div className="mb-4 flex">{viewToggle}</div>}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col gap-6 items-center">
            <div className="p-6 rounded-full bg-yellow-5 dark:bg-yellowdark-5 text-yellow-normal">
              <ExclamationTriangleIcon className="h-12 w-12" />
            </div>
            <div className="flex flex-col gap-2 items-center">
              <span className="text-2xl">No feeds!</span>
              <span className="text-slate-dim">
                {viewing === "shared"
                  ? "Nobody has shared a feed with you yet."
                  : "You haven't created any feeds yet!"}
              </span>
            </div>
            <div className="flex gap-2">
              <ButtonLink
                to="/docs/$slug"
                params={{ slug: "intro" }}
                variant="outline"
              >
                Help Guides
              </ButtonLink>
              <ButtonLink to="/app/create/choice">Create a feed</ButtonLink>
            </div>
          </div>
        </div>
      </>
    )
  );
}

function HomeComponent() {
  return (
    <Page className="md:gap-10">
      <DashboardBanners />
      <RateLimitBanner />
      <FeedTable />
    </Page>
  );
}
